import { newsCategories as fallbackCategories, newsItems as fallbackItems } from "@/mocks/newsData";

export type NewsItem = {
  id: number;
  newsCategoryId: number | null;
  category?: string | null;
  slug: string;
  title: string;
  content?: string;
  publishedAt: string | null;
  date: string;
  isPublished: boolean;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type NewsItemPayload = {
  NewsCategoryId: number;
  Slug: string;
  Title: string;
  Content: string;
  PublishedAt: string | null;
  IsPublished: boolean;
  SortOrder: number;
};

export type NewsCategory = {
  id: number;
  name: string;
  description?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type NewsCategoryPayload = {
  Name: string;
  Description: string;
  IsActive: boolean;
  SortOrder: number;
};

export type NewsCategoryOption = {
  id: number;
  name: string;
};

type ApiNewsItem = {
  NewsItemId: string | number;
  NewsCategoryId?: string | number | null;
  Category?: string | null;
  Slug: string;
  Title: string;
  Content?: string;
  PublishedAt?: string | null;
  IsPublished?: boolean;
  SortOrder?: string | number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

type ApiNewsCategory = {
  NewsCategoryId: string | number;
  Name: string;
  Description?: string | null;
  IsActive?: boolean;
  SortOrder?: string | number;
  CreatedAt?: string | null;
  UpdatedAt?: string | null;
};

type ApiResponse<T> = T & {
  ok: boolean;
  message?: string;
};

const adminCsrfKey = "fulling_admin_csrf";

function getAdminCsrfToken() {
  return sessionStorage.getItem(adminCsrfKey) || "";
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as ApiResponse<T>;
  if (!response.ok || data.ok === false) {
    throw new Error(data.message || "資料處理失敗，請稍後再試。");
  }
  return data as T;
}

function adminHeaders(json = true) {
  return {
    Accept: "application/json",
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(getAdminCsrfToken() ? { "X-CSRF-Token": getAdminCsrfToken() } : {}),
  };
}

function formatDate(value?: string | null) {
  if (!value) return "";
  return value.slice(0, 10);
}

function normalizeItem(item: ApiNewsItem): NewsItem {
  return {
    id: Number(item.NewsItemId),
    newsCategoryId: item.NewsCategoryId ? Number(item.NewsCategoryId) : null,
    category: item.Category || null,
    slug: item.Slug || String(item.NewsItemId),
    title: item.Title || "",
    content: item.Content,
    publishedAt: item.PublishedAt || null,
    date: formatDate(item.PublishedAt || item.CreatedAt),
    isPublished: item.IsPublished !== false,
    sortOrder: Number(item.SortOrder || 100),
    createdAt: item.CreatedAt || null,
    updatedAt: item.UpdatedAt || null,
  };
}

function normalizeCategory(category: ApiNewsCategory): NewsCategory {
  return {
    id: Number(category.NewsCategoryId),
    name: category.Name || "",
    description: category.Description || "",
    isActive: category.IsActive !== false,
    sortOrder: Number(category.SortOrder || 100),
    createdAt: category.CreatedAt || null,
    updatedAt: category.UpdatedAt || null,
  };
}

function normalizeMockItem(item: (typeof fallbackItems)[number]): NewsItem {
  return {
    id: item.id,
    newsCategoryId: null,
    category: item.category,
    slug: item.slug,
    title: item.title,
    content: item.content,
    publishedAt: item.publishedAt,
    date: formatDate(item.publishedAt),
    isPublished: true,
    sortOrder: item.id * 10,
  };
}

export async function fetchPublicNews(params: { NewsCategoryId?: string; Keyword?: string; Page?: number; PerPage?: number } = {}) {
  try {
    const query = new URLSearchParams();
    if (params.NewsCategoryId) query.set("NewsCategoryId", params.NewsCategoryId);
    if (params.Keyword) query.set("Keyword", params.Keyword);
    if (params.Page) query.set("Page", String(params.Page));
    if (params.PerPage) query.set("PerPage", String(params.PerPage));
    const suffix = query.toString() ? `?${query.toString()}` : "";
    const data = await fetch(`/api/news${suffix}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{
      ok: boolean;
      Items: ApiNewsItem[];
      Categories?: ApiNewsCategory[];
      Pagination: { Page: number; PerPage: number; Total: number; TotalPages: number };
    }>(response));

    return {
      items: (data.Items || []).map(normalizeItem),
      categories: (data.Categories || []).map((category) => ({ id: Number(category.NewsCategoryId), name: category.Name })) as NewsCategoryOption[],
      pagination: data.Pagination,
    };
  } catch {
    let items = fallbackItems.map(normalizeMockItem);
    if (params.NewsCategoryId) {
      const categoryName = fallbackCategories[Number(params.NewsCategoryId) - 1];
      items = items.filter((item) => item.category === categoryName);
    }
    return {
      items,
      categories: fallbackCategories.map((name, index) => ({ id: index + 1, name })),
      pagination: { Page: 1, PerPage: items.length || 10, Total: items.length, TotalPages: 1 },
    };
  }
}

export async function fetchPublicNewsItem(newsItemKey: string | number) {
  try {
    const data = await fetch(`/api/news/${newsItemKey}`, {
      credentials: "include",
      headers: { Accept: "application/json" },
    }).then((response) => parseJsonResponse<{ ok: boolean; Item: ApiNewsItem; RelatedItems?: ApiNewsItem[] }>(response));

    return {
      item: normalizeItem(data.Item),
      relatedItems: (data.RelatedItems || []).map(normalizeItem),
    };
  } catch {
    const items = fallbackItems.map(normalizeMockItem);
    const item = items.find((entry) => entry.id === Number(newsItemKey) || entry.slug === String(newsItemKey)) || null;
    if (!item) return { item: null, relatedItems: [] };
    const relatedItems = items.filter((entry) => entry.id !== item.id && entry.category === item.category).slice(0, 3);
    return { item, relatedItems };
  }
}

export async function listAdminNewsCategories() {
  const data = await fetch("/api/admin/news-categories", {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Categories: ApiNewsCategory[] }>(response));
  return data.Categories.map(normalizeCategory);
}

export async function saveAdminNewsCategory(categoryId: number | null, payload: NewsCategoryPayload) {
  const path = categoryId ? `/api/admin/news-categories/${categoryId}` : "/api/admin/news-categories";
  const method = categoryId ? "PUT" : "POST";
  const data = await fetch(path, {
    method,
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Category: ApiNewsCategory }>(response));
  return normalizeCategory(data.Category);
}

export async function deleteAdminNewsCategory(categoryId: number) {
  return fetch(`/api/admin/news-categories/${categoryId}`, {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(false),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}

export async function listAdminNewsItems(params: { Status?: string; NewsCategoryId?: string; Keyword?: string; Page?: number; PerPage?: number }) {
  const query = new URLSearchParams();
  if (params.Status) query.set("Status", params.Status);
  if (params.NewsCategoryId) query.set("NewsCategoryId", params.NewsCategoryId);
  if (params.Keyword) query.set("Keyword", params.Keyword);
  if (params.Page) query.set("Page", String(params.Page));
  if (params.PerPage) query.set("PerPage", String(params.PerPage));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  const data = await fetch(`/api/admin/news-items${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{
    ok: boolean;
    Items: ApiNewsItem[];
    Counts?: { Published: number; Hidden: number };
    Categories?: ApiNewsCategory[];
    Pagination: { Page: number; PerPage: number; Total: number; TotalPages: number };
  }>(response));

  return {
    ...data,
    Items: data.Items.map(normalizeItem),
    Categories: (data.Categories || []).map((category) => ({ id: Number(category.NewsCategoryId), name: category.Name })) as NewsCategoryOption[],
  };
}

export async function getAdminNewsItem(newsItemId: string | number) {
  const data = await fetch(`/api/admin/news-items/${newsItemId}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  }).then((response) => parseJsonResponse<{ ok: boolean; Item: ApiNewsItem }>(response));
  return normalizeItem(data.Item);
}

export async function saveAdminNewsItem(newsItemId: string | number | null, payload: NewsItemPayload) {
  const path = newsItemId ? `/api/admin/news-items/${newsItemId}` : "/api/admin/news-items";
  const method = newsItemId ? "PUT" : "POST";
  const data = await fetch(path, {
    method,
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify(payload),
  }).then((response) => parseJsonResponse<{ ok: boolean; Item: ApiNewsItem }>(response));
  return normalizeItem(data.Item);
}

export async function deleteAdminNewsItem(newsItemId: string | number) {
  return fetch(`/api/admin/news-items/${newsItemId}`, {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(false),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}

export async function uploadAdminNewsImage(file: File) {
  const formData = new FormData();
  formData.append("Image", file);
  const data = await fetch("/api/admin/news-items/upload-image", {
    method: "POST",
    credentials: "include",
    headers: adminHeaders(false),
    body: formData,
  }).then((response) => parseJsonResponse<{ ok: boolean; Url: string }>(response));
  return data.Url;
}

export async function deleteAdminNewsImage(url: string) {
  return fetch("/api/admin/news-items/upload-image", {
    method: "DELETE",
    credentials: "include",
    headers: adminHeaders(true),
    body: JSON.stringify({ Url: url }),
  }).then((response) => parseJsonResponse<{ ok: boolean }>(response));
}
